import React, { useState, useEffect } from 'react';
import { useConfirmation } from '../hooks/useModals';
import Button from './ui/Button';
import Input from './ui/Input';
import ConfirmationModal from './ui/ConfirmationModal';
import { getItemTypeTranslation } from '../utils/itemTranslations';

const EditItemModal = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  onRemove,
  characterItem,
  loading = false 
}) => {
  const { modalState: confirmState, confirm, close: closeConfirm } = useConfirmation();
  const [formData, setFormData] = useState({
    enhancement: 0
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (characterItem) {
      setFormData({
        enhancement: characterItem.enhancement || 0
      });
    }
  }, [characterItem]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'enhancement' ? parseInt(value) || 0 : value)
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.enhancement < 0 || formData.enhancement > 25) {
      newErrors.enhancement = 'Level deve estar entre 0 e 25';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleRemove = async () => {
    const confirmed = await confirm({
      title: 'Remover Item',
      message: 'Tem certeza que deseja remover este item do personagem? Esta ação não pode ser desfeita.',
      confirmText: 'Remover',
      cancelText: 'Cancelar',
      type: 'danger',
      icon: '🗑️'
    });

    if (confirmed) {
      onRemove();
    }
  };

  const resetForm = () => {
    setFormData({
      enhancement: 0
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen || !characterItem) return null;



  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-white/10 shadow-2xl max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">Editar Item</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full w-8 h-8 flex items-center justify-center transition-colors text-2xl"
              disabled={loading}
            >
              ×
            </button>
          </div>

          {/* Item Info */}
          <div className="mb-6 p-4 bg-white/5 rounded-lg">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-medium text-white">{characterItem.item.name}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">{characterItem.item.description}</p>
            <p className="text-xs text-gray-500">Tipo: {getItemTypeTranslation(characterItem.item.type)}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="enhancement" className="block text-sm font-medium text-gray-300 mb-1">
                Level
              </label>
              <Input
                id="enhancement"
                name="enhancement"
                type="number"
                min="0"
                max="25"
                value={formData.enhancement}
                onChange={handleChange}
                disabled={loading}
                placeholder="0 = sem level, 25 = máximo"
              />
              {errors.enhancement && (
                <p className="text-red-400 text-sm mt-1">{errors.enhancement}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Level atual: +{characterItem.enhancement || 0}
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <div className="flex-1">
                <Button
                  type="button"
                  variant="danger"
                  onClick={handleRemove}
                  disabled={loading}
                  fullWidth
                >
                  Remover Item
                </Button>
              </div>
              <div className="flex-1">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleClose}
                  disabled={loading}
                  fullWidth
                >
                  Cancelar
                </Button>
              </div>
              <div className="flex-1">
                <Button
                  type="submit"
                  loading={loading}
                  fullWidth
                >
                  {loading ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmState.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
        icon={confirmState.icon}
      />
    </div>
  );
};

export default EditItemModal;